import React, { useMemo } from 'react';
import {
  Accordion,
  AccordionSummary,
  Alert,
  List,
  ListItemText,
  ListItem as MUIListItem,
  Typography,
} from '@mui/material';
import { ListEntitiesSearchProps } from '../ListEntitiesSearch';
import { useAppSelector } from '../../../../redux/store.model';
import {
  getAllGroupsInWorkspaceWithChainGroups,
  getAllGroupsWithChainGroups,
} from '../../../../redux/groups/selectors/searchSelectors';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import { getPolyglot } from '../../../../i18n';
import { ListItem } from '../../../../theme/components';
import GroupModel from '../../../../redux/groups/api/GroupModel';
import { DeviceGroupIcon } from '../../../../theme/icons';
import { useNavigate } from 'react-router-dom';
import { getMyWorkspaceSelector } from '../../../../redux/stoerkID/selectors/StoerkId.selectors';
import { isSCCService } from '../../../../customized/fresh-watch/redux/rights/selectors/roles';
import { BusinessRole } from '../../../../customized/fresh-watch/redux/businessModel/model/model';
import { Base64 } from '../../../../util/Base64';

function GroupsSearch(props: ListEntitiesSearchProps) {
  const { searchValue, close } = props;
  const groups = useAppSelector(getAllGroupsWithChainGroups);
  const workspaceId = useAppSelector(getMyWorkspaceSelector)?.id ?? '';
  let groupsInWorkspace = useAppSelector((state) =>
    getAllGroupsInWorkspaceWithChainGroups(state, { workspaceId })
  );
  const featureFlag = useAppSelector((state) => state.featureToggle);
  const isFreshwatch = featureFlag.styledComponentsVariants === 'fresh-watch';
  const isAdmin = useAppSelector(isSCCService);
  if (isFreshwatch) {
    if (!isAdmin) {
      groupsInWorkspace = [];
    }
  } else {
    groupsInWorkspace = [];
  }
  const navigate = useNavigate();

  const groupsResults = useMemo(
    () =>
      [...groups, ...groupsInWorkspace].filter(
        (group) =>
          group.name &&
          searchValue &&
          searchValue?.length &&
          group.name.toLowerCase().includes(searchValue?.toLowerCase())
      ), // Limit to 10 results
    [groups, searchValue, groupsInWorkspace]
  );

  const groupsResultsLimited = useMemo(
    () => groupsResults.slice(0, 100),
    [groupsResults]
  );
  const nGroupsLimited = groupsResults.length - groupsResultsLimited.length;

  const polyglot = getPolyglot();

  const onClick = (group: any) => () => {
    const url = `/devicemanager/group/${group.id}`;
    if (isFreshwatch) {
      onNavigateToMerchandiser(group);
    } else {
      navigate(url);
    }
    close?.();
  };

  const onNavigateToMerchandiser = (group: any) => {
    if (group?.attributes?.['BUSINESS_ROLE'] === BusinessRole.STORE) {
      const groupId = group.id;
      try {
        navigate(`/merchandiser/${groupId}`);
      } catch (error) {
        console.error('Error decoding group ID:', error);
      }
    } else {
      navigate(`/storelist/group/${group?.id}`);
    }
  };

  return (
    <Accordion defaultExpanded={true}>
      <AccordionSummary
        id="accordion-search-groups-title"
        aria-controls="accordion-search-groups-title"
        expandIcon={<ExpandMoreIcon />}
      >
        <Typography variant="h5">
          {polyglot.t('group.groups')} ({groupsResults.length})
        </Typography>
      </AccordionSummary>
      {groupsResultsLimited.length ? (
        <List>
          {groupsResultsLimited.map((group, i) => (
            <ListItem
              key={i}
              name={group?.name}
              iconURL={group?.iconURL}
              subtitle={
                group?.chainGroups &&
                GroupModel.getGroupChainsSubtitles(group?.chainGroups as any)
              }
              iconNode={!group?.iconURL && <DeviceGroupIcon />}
              className={GroupModel.getClassesNamesFromDevice(group as any)}
              onClick={group && onClick(group)}
              ItemTextContainerProps={{
                sx: (theme) => ({
                  maxWidth: 'initial',
                  textAlign: 'left',
                  [theme.breakpoints.down('md')]: {
                    maxWidth: 'initial',
                  },
                }),
              }}
              TitleProps={{
                sx: (theme) => ({
                  maxWidth: '500px',
                  [theme.breakpoints.down('md')]: {
                    maxWidth: '200px',
                  },
                }),
              }}
            />
          ))}
          {groupsResults.length > 100 && (
            <MUIListItem>
              <ListItemText
                primary={`${nGroupsLimited} ${polyglot.t(
                  'device.search.message_results_limited'
                )}`}
              />
            </MUIListItem>
          )}
        </List>
      ) : (
        <Alert severity="info">
          {polyglot.t('device.search.message_no_results_found')}
        </Alert>
      )}
    </Accordion>
  );
}

export default React.memo(GroupsSearch);
